"use client";

import { ModalsProvider } from "@mantine/modals";
import {
  AppProvider,
  CrispChatWidget,
  ShellLayout,
  useAppContext,
  type QuickAction,
} from "@repo/dashboard-core";
import { useParams, useRouter } from "next/navigation";
import { useMemo } from "react";

import { AuthHydrator } from "@repo/dashboard-core/auth";
import { useFileUpload } from "@/components/my-files/UploadCta";
import {
  openSubscriptionModal,
  SubscriptionModal,
} from "@/components/subscription/SubscriptionModal";
import { TourLauncher } from "@/components/tour/TourLauncher";
import type { PdfBrandConfig } from "@/config/brand";
import { CRISP_WEBSITE_ID } from "@/config/crisp";
import { routeMap } from "@/config/routes";
import { quickActions, shellConfig } from "@/config/shell";
import { BrandProvider, useBrand } from "@/lib/context/BrandContext";

function InnerShell({ children }: { children: React.ReactNode }) {
  const params = useParams<{ locale: string }>();
  const router = useRouter();
  const { subscriptionsInfo, userInfo } = useAppContext();
  const brand = useBrand();

  // Upload File quick action: same flow as the My Files "Add New" button —
  // pick a file, upload it, then open it in the editor.
  const { inputRef, handleChange, openPicker } = useFileUpload(
    (storedFileName) =>
      router.push(
        `/${params.locale}/editor?name=${encodeURIComponent(storedFileName)}`,
      ),
  );

  const resolvedQuickActions: QuickAction[] = [
    {
      icon: "uploadCloud",
      titleKey: "quick-actions.upload-file",
      descriptionKey: "quick-actions.upload-file-desc",
      onSelect: openPicker,
    },
    ...quickActions,
  ];

  const resolvedConfig = useMemo(() => {
    const base = {
      ...shellConfig,
      brand,
      headerExtra: <TourLauncher />,
    };

    const subscriptions = subscriptionsInfo?.runningSubscriptions;
    if (!subscriptions?.length) return base;

    return {
      ...base,
      onManageSubscription: () =>
        openSubscriptionModal({
          subscriptions,
          customerEmail: userInfo.email,
        }),
    };
  }, [brand, subscriptionsInfo?.runningSubscriptions, userInfo.email]);

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={handleChange}
        aria-hidden
        tabIndex={-1}
      />
      <ShellLayout
        locale={params.locale}
        config={resolvedConfig}
        routeMap={routeMap}
        quickActions={resolvedQuickActions}
      >
        {children}
      </ShellLayout>
      <CrispChatWidget websiteId={CRISP_WEBSITE_ID} locale={params.locale} />
    </>
  );
}

export function DashboardShell({
  brand,
  children,
}: {
  brand: PdfBrandConfig;
  children: React.ReactNode;
}) {
  return (
    <AppProvider>
      <AuthHydrator>
        <BrandProvider brand={brand}>
          <ModalsProvider modals={{ subscription: SubscriptionModal }}>
            <InnerShell>{children}</InnerShell>
          </ModalsProvider>
        </BrandProvider>
      </AuthHydrator>
    </AppProvider>
  );
}
